import '../TweakAdvancedSettings/TweakAdvancedSettingsForm.scss';
import { LabelWithDescription } from '@/components/LabelWithdescription';
import {
	AccessAlarmsOutlined,
	DeleteOutlineOutlined,
	VoicemailOutlined,
} from '@mui/icons-material';
import {
	Button,
	Form,
	FormInstance,
	Select,
	Slider,
	Switch,
	Tabs,
	Upload,
	notification,
} from 'antd';
import React, { useMemo } from 'react';
import { EAgentSettings } from 'src/enums/EAgentSettings';
import { EDatabase } from 'src/enums/EDatabase';
import EllipsisText from '@/components/LabelWithdescription/EllipsisText';
import { useMutation, useQuery } from '@tanstack/react-query';
import { deleteVoicemailtone, getVoicemailTones, uploadVoicemailTone } from '@/api/voice';
import { ITweakAdvancedSettingsForm } from '@/interfaces/ITweakAdvancedSettingsForm';
import { UploadOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import type { RcFile, UploadRequestOption } from 'rc-upload/lib/interface';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getSelectedAgent, getSelectedAgentId } from '@/store/account/selector';
import showConfirmDeleteModal from '@/components/ConfirmDeleteModal/ConfirmDeleteModal';
import VoiceAudioPlayer from './AudioPlayer';

interface VoicemailToneProps {
	selectedVoicemailTone: string | null;
	setSelectedVoicemailTone: (val: string) => void;
	activeField: string | null;
	form: FormInstance<ITweakAdvancedSettingsForm>;
	handleSubmitVoicemailToneSelect: (val: string) => void;
}

const VoicemailSettings: React.FC<VoicemailToneProps> = ({
	selectedVoicemailTone,
	setSelectedVoicemailTone,
	activeField,
	form,
	handleSubmitVoicemailToneSelect,
}) => {
	const baseClass = 'tweak-advanced-settings-form';

	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const selectedAgent = useAppSelector(getSelectedAgent);

	const { data: voicemailTonesGlobal, refetch: refetchGlobal } = useQuery({
		queryKey: ['voicemailTonesGlobal'],
		queryFn: () => getVoicemailTones({ agentId: selectedAgentId }),
		staleTime: 1000 * 60 * 60, // 1 hour
		refetchOnWindowFocus: false,
		refetchOnMount: false,
	});
	const { data: voicemailTones, refetch } = useQuery({
		queryKey: ['voicemailTones'],
		queryFn: () => getVoicemailTones({ agentId: selectedAgentId, myVoice: true }),
		staleTime: 1000 * 60 * 60, // 1 hour
		refetchOnWindowFocus: false,
		refetchOnMount: false,
	});

	const sortedVoicemailTonesGlobal = useMemo(() => {
		return [...(voicemailTonesGlobal?.data?.data || [])].sort((a, b) => {
			if (a.fileKey === selectedVoicemailTone) {
				return -1;
			}
			if (b.fileKey === selectedVoicemailTone) {
				return 1;
			}
			return 0;
		});
	}, [voicemailTonesGlobal?.data.data, selectedVoicemailTone]);

	const sortedVoicemailTones = useMemo(() => {
		return [...(voicemailTones?.data?.data || [])].sort((a, b) => {
			if (a.fileKey === selectedVoicemailTone) {
				return -1;
			}
			if (b.fileKey === selectedVoicemailTone) {
				return 1;
			}
			return 0;
		});
	}, [voicemailTones?.data.data, selectedVoicemailTone]);

	const { mutate: uploadVoicemailToneMutation } = useMutation({
		mutationKey: ['upload-voicemail-tone'],
		mutationFn: uploadVoicemailTone,
		onSuccess: (data) => {
			notification.success({
				message: data?.message || 'Voice cloned and attached to your receptionist successfully!',
			});

			selectedAgent?.isAdmin ? refetchGlobal() : refetch();
		},
		onError: (error: any) => {
			notification.error({
				message: error.message || 'An error occurred during the upload.',
			});
		},
	});

	const { mutateAsync: deleteVoicemailMutation } = useMutation({
		mutationKey: ['deleteVoicemail'],
		mutationFn: deleteVoicemailtone,
		onSuccess: ({ success }) => {
			if (success) {
				refetch();
			}
		},
		onError() {
			notification.error({ message: 'Some error occured during Voicemail updation' });
		},
	});

	const handleVoicemailAfterNDurationChange = (value: number) => {
		form.setFieldsValue({ recordVoicemailAfterNSec: value });
	};

	const handleConfirmDelete = (fileKey: string) => {
		showConfirmDeleteModal({
			itemName: `Voicemail "${fileKey}"`,
			onConfirm: () => handleDeleteVoicemail(fileKey),
			title: 'Are you sure you want to delete this Voicemail?',
		});
	};

	const handleDeleteVoicemail = async (fileKey: string) => {
		try {
			await deleteVoicemailMutation({ fileKey, agentId: selectedAgentId });

			notification.success({
				message: `"${fileKey}" deleted successfully.`,
			});
		} catch {
			notification.error({
				message: 'Something went wrong, please try again later!',
			});
		}
	};

	const props: UploadProps = {
		accept: 'audio/wav, audio/mpeg',
		name: 'file',
		customRequest: async (options: UploadRequestOption) => {
			const { file, onSuccess, onError } = options;
			const selectedFile = file as RcFile;

			const maxSize = 1 * 1000000; // 1 MB
			const validTypes = ['audio/wav', 'audio/x-wav', 'audio/mpeg'];

			if (selectedFile.size > maxSize) {
				notification.error({ message: 'File size should not exceed 1 MB.' });
				onError?.(new Error('File too large'));
				return;
			}

			if (!validTypes.includes(selectedFile.type)) {
				notification.error({ message: 'Invalid file type. Only .mp3 or .wav files are allowed.' });
				onError?.(new Error('Invalid file type'));
				return;
			}

			const formData = new FormData();
			formData.append('file', selectedFile);

			try {
				await uploadVoicemailToneMutation({ payload: formData, agentId: selectedAgentId });
				onSuccess?.(formData, selectedFile); // Tell AntD the upload succeeded
			} catch (error: any) {
				onError?.(error);
			}
		},
	};

	return (
		<>
			{activeField === EAgentSettings.ALLOW_VOICEMAILS || activeField === null ? (
				<Form.Item
					className={
						activeField === EAgentSettings.ALLOW_VOICEMAILS
							? `${baseClass}_active-class`
							: `${baseClass}__item-container`
					}
				>
					<div className={`${baseClass}__details-container flex flex-col pr-4 mb-8`}>
						<div className="flex items-start justify-between">
							<LabelWithDescription
								customClassName="w-full"
								label="Allow Voicemails"
								description='If someone asks to leave a message or voicemail, the AI Receptionist will collect a voicemail. This message will be separately shown in the "Voicemails" section of the dashboard.'
								icon={<VoicemailOutlined className="mr-1 mb-1" />}
								showDescriptionDirectly={true}
							/>
							<Form.Item name={'voicemails_enabled'} valuePropName="checked">
								<Switch size="small" />
							</Form.Item>
						</div>

						<Form.Item
							noStyle
							shouldUpdate={(prevValues, currentValues) =>
								prevValues.voicemails_enabled !== currentValues.voicemails_enabled
							}
						>
							{({ getFieldValue }) => {
								const isEnabled = getFieldValue('voicemails_enabled');
								return (
									isEnabled && (
										<>
											<Form.Item
												className={`${baseClass}__item-container`}
												name="recordVoicemailAfterNSec"
												label={
													<LabelWithDescription
														customClassName="w-full"
														label="Record Voicemail After Silence (N Sec)"
														description="Specify the number of seconds of silence after the caller finishes speaking, after which the system will assume the voicemail recording is complete."
														icon={<AccessAlarmsOutlined className="mr-1 mb-1" />}
														showDescriptionDirectly={true}
													/>
												}
											>
												<Slider
													min={1}
													max={10}
													className="mx-4"
													value={form.getFieldValue('recordVoicemailAfterNSec')}
													onChange={handleVoicemailAfterNDurationChange}
												/>
											</Form.Item>

											<Form.Item label="Voicemail Prompt" name="voicemail_prompt_type">
												<Select
													className="w-full max-w-md"
													placeholder="Select a voicemail prompt"
													defaultValue="default"
													options={[
														{
															value: 'default',
															label: 'Leave your message in 3... 2... 1 (default)',
														},
														{ value: 'tone', label: 'Please leave your message after this tone' },
													]}
												/>
											</Form.Item>

											<Form.Item noStyle shouldUpdate>
												{({ getFieldValue }) => {
													const promptType = getFieldValue('voicemail_prompt_type');
													return (
														promptType === 'tone' && (
															<Tabs
																defaultActiveKey="All"
																className="mt-1"
																items={[
																	{
																		key: 'All',
																		label: 'All Voicemail Tones',
																		children: (
																			<div>
																				{selectedAgent?.isAdmin && (
																					<Upload {...props}>
																						<Button icon={<UploadOutlined />}>Add Voicemail</Button>
																					</Upload>
																				)}
																				<div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 pr-4">
																					{sortedVoicemailTonesGlobal.length > 0 &&
																						sortedVoicemailTonesGlobal.map((voice) => (
																							<div
																								key={voice.fileKey}
																								className="relative group flex p-2 items-center justify-between border w-full rounded-lg bg-white shadow-sm hover:shadow-xl transition-shadow"
																							>
																								<div
																									onClick={(e) => e.stopPropagation()}
																									className="mr-2"
																								>
																									<VoiceAudioPlayer
																										fileName={voice.fileKey}
																										db={EDatabase.VOICEMAIL_TONES}
																										onPlay={() =>
																											console.log('Audio started playing')
																										}
																										onPause={() => console.log('Audio paused')}
																									/>
																								</div>
																								<EllipsisText text={voice.fileName} length={15} />
																								<Form.Item
																									name="call_transfer_voicemail_tone_key"
																									noStyle
																								>
																									<div
																										className="cursor-pointer font-semibold hover:bg-blue-50"
																										onClick={() => {
																											setSelectedVoicemailTone(voice.fileKey);
																											form.setFieldsValue({
																												voicemail_tone_key: voice.fileKey,
																											});
																											handleSubmitVoicemailToneSelect(
																												voice.fileKey,
																											);
																										}}
																									>
																										{selectedVoicemailTone === voice.fileKey ? (
																											<div className="border py-1 px-2 text-xs text-red-500 rounded-lg text-center">
																												Selected
																											</div>
																										) : (
																											<div className="border py-1 px-2 text-xs text-gray-500 rounded-lg text-center">
																												Select
																											</div>
																										)}
																									</div>
																								</Form.Item>
																								{selectedAgent?.isAdmin && (
																									<button
																										onClick={(e) => {
																											e.preventDefault();
																											handleConfirmDelete(voice.fileKey);
																										}}
																										className="absolute -top-4 -right-4 opacity-0 group-hover:opacity-100 group-hover:pointer-events-auto pointer-events-none transition-opacity duration-200"
																									>
																										<div className="w-7 h-7 bg-white rounded-full shadow flex items-center justify-center border border-gray-300 hover:bg-red-100">
																											<DeleteOutlineOutlined />
																										</div>
																									</button>
																								)}
																							</div>
																						))}
																				</div>
																			</div>
																		),
																	},
																	{
																		key: 'My',
																		label: 'My Voicemail Tones',
																		children: (
																			<div>
																				<Upload {...props}>
																					<Button icon={<UploadOutlined />}>Add Voicemail</Button>
																				</Upload>
																				<div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 pr-4">
																					{sortedVoicemailTones.length > 0 &&
																						sortedVoicemailTones.map((voice) => (
																							<div
																								key={voice.fileKey}
																								className="relative group flex p-2 items-center justify-between border w-full rounded-lg bg-white shadow-sm hover:shadow-xl transition-shadow"
																							>
																								<div
																									onClick={(e) => e.stopPropagation()}
																									className="mr-2"
																								>
																									<VoiceAudioPlayer
																										fileName={voice.fileKey}
																										db={EDatabase.VOICEMAIL_TONES}
																										onPlay={() =>
																											console.log('Audio started playing')
																										}
																										onPause={() => console.log('Audio paused')}
																									/>
																								</div>
																								<EllipsisText text={voice.fileName} length={15} />
																								<Form.Item
																									name="call_transfer_voicemail_tone_key"
																									noStyle
																								>
																									<div
																										className="cursor-pointer font-semibold hover:bg-blue-50"
																										onClick={() => {
																											setSelectedVoicemailTone(voice.fileKey);
																											form.setFieldsValue({
																												voicemail_tone_key: voice.fileKey,
																											});
																											handleSubmitVoicemailToneSelect(
																												voice.fileKey,
																											);
																										}}
																									>
																										{selectedVoicemailTone === voice.fileKey ? (
																											<div className="border py-1 px-2 text-xs text-red-500 rounded-lg text-center">
																												Selected
																											</div>
																										) : (
																											<div className="border py-1 px-2 text-xs text-gray-500 rounded-lg text-center">
																												Select
																											</div>
																										)}
																									</div>
																								</Form.Item>
																								<button
																									onClick={(e) => {
																										e.preventDefault();
																										handleConfirmDelete(voice.fileKey);
																									}}
																									className="absolute -top-4 -right-4 opacity-0 group-hover:opacity-100 group-hover:pointer-events-auto pointer-events-none transition-opacity duration-200"
																								>
																									<div className="w-7 h-7 bg-white rounded-full shadow flex items-center justify-center border border-gray-300 hover:bg-red-100">
																										<DeleteOutlineOutlined />
																									</div>
																								</button>
																							</div>
																						))}
																				</div>
																			</div>
																		),
																	},
																]}
															/>
														)
													);
												}}
											</Form.Item>
										</>
									)
								);
							}}
						</Form.Item>
					</div>
				</Form.Item>
			) : null}
		</>
	);
};

export default VoicemailSettings;
